import React, { useState, useEffect, useCallback } from 'react';
import { Loader2 } from 'lucide-react';
import { getVisitPdf, client } from '../../services/api';

interface ReadonlyChartViewProps {
  visitId: string;
}

export const ReadonlyChartView: React.FC<ReadonlyChartViewProps> = ({
  visitId,
}) => {
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPdf = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await getVisitPdf({
        client,
        path: { visit_id: visitId },
      });

      if (!response.data) {
        setError('No PDF data received from the server.');
        return;
      }

      const blob = new Blob([response.data as BlobPart], {
        type: 'application/pdf',
      });
      setPdfUrl(prev => {
        if (prev) URL.revokeObjectURL(prev);
        return URL.createObjectURL(blob);
      });
    } catch (err) {
      console.error('Failed to load PDF:', err);
      setError('Failed to load chart review. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [visitId]);

  useEffect(() => {
    fetchPdf();
    return () => {
      setPdfUrl(prev => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
    };
  }, [fetchPdf]);

  if (isLoading) {
    return (
      <div className='h-full w-full flex items-center justify-center border rounded-md'>
        <Loader2 className='h-8 w-8 animate-spin' />
      </div>
    );
  }

  if (error || !pdfUrl) {
    return (
      <div className='h-full w-full flex items-center justify-center border rounded-md text-muted-foreground'>
        <p>{error || 'No PDF available.'}</p>
      </div>
    );
  }

  return (
    <div className='h-full w-full border rounded-md overflow-hidden bg-muted/10'>
      <iframe src={pdfUrl} title='Chart Review' className='h-full w-full' />
    </div>
  );
};
